import React, { useState, useEffect, Fragment } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, Transition } from '@headlessui/react';
import { Bars3Icon, XMarkIcon, UserCircleIcon, ChevronDownIcon, ArrowRightOnRectangleIcon, ChartBarIcon, ShoppingBagIcon, HeartIcon, Cog6ToothIcon } from '@heroicons/react/24/outline';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsOpen(false);
  };

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user || !user.name) return 'U';
    return user.name.charAt(0).toUpperCase();
  };

  // User menu items
  const userMenuItems = [
    { name: 'Dashboard', icon: ChartBarIcon, path: '/dashboard' },
    { name: 'My Bookings', icon: ShoppingBagIcon, path: '/dashboard?tab=bookings' },
    { name: 'Test Rides', icon: HeartIcon, path: '/dashboard?tab=test-rides' }
  ];

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      scrolled ? 'bg-black/95 backdrop-blur-md shadow-lg' : 'bg-black'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="text-2xl font-bold">
              <img src="images/logo.png" alt="Janvaahan Logo" className="logo" />
            </div>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex space-x-8">
            <Link to="/products" className="hover:text-red-500 transition relative group">
              Electric Bikes
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to="/dealership" className="hover:text-red-500 transition relative group">
              Dealership
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 group-hover:w-full transition-all duration-300"></span>
            </Link>
            <Link to="/support" className="hover:text-red-500 transition relative group">
              Support
              <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-red-600 group-hover:w-full transition-all duration-300"></span>
            </Link>
          </div>

          {/* Desktop Buttons */}
          <div className="hidden md:flex items-center space-x-4">
            <Link 
              to="/test-ride" 
              className="border border-white px-4 py-2 rounded-lg hover:bg-white hover:text-black transition transform hover:scale-105"
            >
              Test Ride
            </Link>
        
            {user ? (
              // User is logged in - Show user menu
              <Menu as="div" className="relative ml-4">
                <Menu.Button className="flex items-center space-x-3 focus:outline-none group">
                  <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold group-hover:bg-red-700 transition">
                    {getUserInitials()}
                  </div>
                  <span className="text-sm font-medium hidden lg:block">{user.name.split(' ')[0]}</span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-400 group-hover:text-white transition" />
                </Menu.Button>
                
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg py-2 border border-gray-100 focus:outline-none">
                    {/* User Info */}
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-semibold text-gray-900">{user.name}</p>
                      <p className="text-xs text-gray-500 truncate">{user.email}</p>
                    </div>
                    
                    {/* Menu Items */}
                    {userMenuItems.map((item) => (
                      <Menu.Item key={item.name}>
                        {({ active }) => (
                          <Link
                            to={item.path}
                            className={`flex items-center px-4 py-2 text-sm ${
                              active ? 'bg-gray-50 text-red-600' : 'text-gray-700'
                            }`}
                          >
                            <item.icon className="h-5 w-5 mr-3" />
                            {item.name}
                          </Link>
                        )}
                      </Menu.Item>
                    ))}
                    
                    {/* Logout Button */}
                    <Menu.Item>
                      {({ active }) => (
                        <button
                          onClick={handleLogout}
                          className={`flex items-center w-full px-4 py-2 text-sm border-t border-gray-100 ${
                            active ? 'bg-gray-50 text-red-600' : 'text-gray-700'
                          }`}
                        >
                          <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3" />
                          Sign Out
                        </button>
                      )}
                    </Menu.Item>
                  </Menu.Items>
                </Transition>
              </Menu>
            ) : (
              // User not logged in - Show login/signup
              <div className="flex items-center space-x-3">
                <Link to="/login" className="px-4 py-2 hover:text-red-500 transition">Sign In</Link>
                <Link 
                  to="/register" 
                  className="bg-white text-black px-4 py-2 rounded-lg hover:bg-gray-100 transition transform hover:scale-105"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button 
            onClick={() => setIsOpen(!isOpen)} 
            className="md:hidden p-2 rounded-lg hover:bg-white/10 transition"
          >
            {isOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        <Transition
          show={isOpen}
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="transform opacity-0 -translate-y-2"
          enterTo="transform opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="transform opacity-100 translate-y-0"
          leaveTo="transform opacity-0 -translate-y-2"
        >
          <div className="md:hidden py-4 border-t border-gray-800">
            <div className="space-y-3">
              <Link 
                to="/products" 
                onClick={() => setIsOpen(false)}
                className="block py-2 hover:text-red-500 transition"
              >
                Electric Bikes
              </Link>
              <Link 
                to="/dealership" 
                onClick={() => setIsOpen(false)}
                className="block py-2 hover:text-red-500 transition"
              >
                Dealership
              </Link>
              <Link 
                to="/support" 
                onClick={() => setIsOpen(false)}
                className="block py-2 hover:text-red-500 transition"
              >
                Support
              </Link>
              
              <div className="pt-4 space-y-3">
                <Link 
                  to="/test-ride" 
                  onClick={() => setIsOpen(false)}
                  className="block border border-white px-4 py-2 text-center rounded-lg hover:bg-white hover:text-black transition"
                >
                  Test Ride
                </Link>
              
                
                {user ? (
                  <>
                    <div className="pt-4 border-t border-gray-800">
                      <div className="flex items-center space-x-3 px-4 py-2">
                        <div className="w-10 h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold">
                          {getUserInitials()}
                        </div>
                        <div>
                          <p className="font-semibold">{user.name}</p>
                          <p className="text-xs text-gray-400">{user.email}</p>
                        </div>
                      </div>
                      <Link 
                        to="/dashboard" 
                        onClick={() => setIsOpen(false)}
                        className="block px-4 py-2 hover:text-red-500 transition"
                      >
                        Dashboard
                      </Link>
                      <button 
                        onClick={handleLogout}
                        className="block w-full text-left px-4 py-2 hover:text-red-500 transition"
                      >
                        Sign Out
                      </button>
                    </div>
                  </>
                ) : (
                  <div className="flex space-x-3 pt-2">
                    <Link 
                      to="/login" 
                      onClick={() => setIsOpen(false)}
                      className="flex-1 px-4 py-2 text-center border border-white rounded-lg hover:bg-white hover:text-black transition"
                    >
                      Sign In
                    </Link>
                    <Link 
                      to="/register" 
                      onClick={() => setIsOpen(false)}
                      className="flex-1 bg-red-600 px-4 py-2 text-center rounded-lg hover:bg-red-700 transition"
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Transition>
      </div>
    </nav>
  );
};

export default Navbar;